import { useEffect, useState } from "react";
import {
  deciderIncident,
  getCcjaPrecedents,
  getCompositionTribunal,
  getIncidents,
  souleverIncident,
} from "@/lib/apiProcedure";
import type { Role } from "@/contexts/AuthContext";
import type {
  CcjaPrecedentLight,
  IncidentProcedural,
  MembreTribunal,
  StatutIncident,
  TypeIncident,
} from "@/types/procedure";

interface Props {
  caseId: string | number;
  /** Membre du dossier : peut soulever un incident. */
  canRaise?: boolean;
  /** Rôle du connecté : décide selon le type d'incident (compétence). */
  role?: Role | null;
}

function apiDetail(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { detail?: string } } })?.response?.data
      ?.detail ?? fallback
  );
}

const STATUT_STYLE: Record<StatutIncident, string> = {
  SOULEVEE: "bg-amber-100 text-amber-800",
  ADMISE: "bg-green-100 text-green-800",
  REJETEE: "bg-red-100 text-red-800",
  IRRECEVABLE: "bg-gray-200 text-gray-700",
};

const TYPE_LABEL: Record<TypeIncident, string> = {
  RECUSATION: "Récusation d'arbitre",
  EXCEPTION_INCOMPETENCE: "Exception d'incompétence",
};

// Rôles visés par une récusation (cf. ARBITRE_ROLES backend) : on exclut le
// secrétaire et les parties/conseils.
const ARBITRE_ROLES = new Set([
  "ARBITRE",
  "ARBITRE_PRESIDENT",
  "ARBITRE_COARBITRE",
]);
const ROLE_LABEL: Record<string, string> = {
  ARBITRE: "Arbitre",
  ARBITRE_PRESIDENT: "Président",
  ARBITRE_COARBITRE: "Co-arbitre",
};

/** Autorité compétente pour trancher selon le type (cf. backend incidents.py). */
function peutTrancher(incident: IncidentProcedural, role?: Role | null): boolean {
  if (role === "ADMIN") return true;
  if (incident.incident_type === "RECUSATION") return role === "GREFFIER";
  return role === "ARBITRE"; // EXCEPTION_INCOMPETENCE → compétence-compétence
}

/**
 * Procédures incidentes (L8) — récusation (AUA art. 6-7) & exception
 * d'incompétence (AUA art. 5). Une partie soulève ; l'autorité compétente
 * tranche. La recommandation consultative et le précédent CCJA fondant la
 * décision sont exposés au décideur.
 */
export function IncidentsPanel({ caseId, canRaise, role }: Props) {
  const [incidents, setIncidents] = useState<IncidentProcedural[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const [type, setType] = useState<TypeIncident>("RECUSATION");
  const [motif, setMotif] = useState("");
  const [arbitreCible, setArbitreCible] = useState("");
  const [viseTribunal, setViseTribunal] = useState(false);
  const [fondement, setFondement] = useState("");

  const [decId, setDecId] = useState<string | null>(null);
  const [decChoice, setDecChoice] = useState("ADMISE");
  const [decMotif, setDecMotif] = useState("");

  // Seul le tribunal (arbitre/admin) voit la suggestion jurisprudentielle.
  const canSeeSuggestions = role === "ARBITRE" || role === "ADMIN";
  // Dictionnaire CCJA indexé par `ref` (= precedent_ref de l'incident).
  const [precedentsByRef, setPrecedentsByRef] = useState<
    Record<string, CcjaPrecedentLight>
  >({});
  // Arbitres du tribunal : alimente le sélecteur « arbitre visé » (récusation).
  const [arbitres, setArbitres] = useState<MembreTribunal[]>([]);

  const load = () => {
    getIncidents(caseId)
      .then((res) => setIncidents(res.data))
      .catch(() => setIncidents([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  // Hydrate la suggestion : on charge le dictionnaire léger une fois, puis on
  // joint par `ref` côté front. Le panneau pouvant mêler récusations et
  // exceptions d'incompétence, on récupère tout le dictionnaire (6 arrêts)
  // plutôt que de filtrer par un seul type_incident.
  useEffect(() => {
    if (!canSeeSuggestions) return;
    getCcjaPrecedents()
      .then((res) =>
        setPrecedentsByRef(
          Object.fromEntries(res.data.map((p) => [p.ref, p])),
        ),
      )
      .catch(() => setPrecedentsByRef({}));
  }, [canSeeSuggestions]);

  // Composition du tribunal pour le sélecteur de récusation (membre du dossier
  // requis côté back ; le conseil l'est). Chargée seulement si la saisine est
  // ouverte au connecté.
  useEffect(() => {
    if (!canRaise) return;
    getCompositionTribunal(caseId)
      .then((res) =>
        setArbitres(res.data.membres.filter((m) => ARBITRE_ROLES.has(m.member_role))),
      )
      .catch(() => setArbitres([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, canRaise]);

  const wrap = async (fn: () => Promise<unknown>, fallback: string) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      load();
      return true;
    } catch (e) {
      setErr(apiDetail(e, fallback));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleSoulever = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await wrap(
      () =>
        souleverIncident(caseId, {
          incident_type: type,
          motif,
          arbitre_cible:
            type === "RECUSATION" && !viseTribunal
              ? arbitreCible.trim() || undefined
              : undefined,
          vise_tout_le_tribunal: type === "RECUSATION" ? viseTribunal : undefined,
          fondement_textuel: fondement.trim() || undefined,
        }),
      "Échec du soulèvement de l'incident.",
    );
    if (ok) {
      setOpen(false);
      setMotif("");
      setArbitreCible("");
      setViseTribunal(false);
      setFondement("");
      setType("RECUSATION");
    }
  };

  const handleDecider = async (id: string) => {
    const ok = await wrap(
      () =>
        deciderIncident(caseId, id, {
          decision: decChoice,
          motif: decMotif.trim() || undefined,
        }),
      "Échec de la décision.",
    );
    if (ok) {
      setDecId(null);
      setDecMotif("");
      setDecChoice("ADMISE");
    }
  };

  if (loading) return null;

  return (
    <div className="border rounded-lg p-4 mt-6 bg-white">
      <h3 className="text-lg font-bold text-gray-800 mb-1">
        Procédures incidentes
      </h3>
      <p className="text-xs text-gray-500 mb-4">
        Récusation & exception d'incompétence — AUA art. 5 à 7.
      </p>

      {err && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-red-800 text-sm">
          {err}
        </div>
      )}

      {canRaise && !open && (
        <button
          onClick={() => setOpen(true)}
          className="mb-4 bg-blue-600 text-white px-3 py-1.5 rounded text-sm"
        >
          Soulever un incident
        </button>
      )}

      {canRaise && open && (
        <form
          onSubmit={handleSoulever}
          className="mb-5 p-3 bg-gray-50 rounded space-y-2"
        >
          <select
            value={type}
            onChange={(e) => setType(e.target.value as TypeIncident)}
            className="w-full rounded border p-2 text-sm bg-white text-gray-900"
          >
            <option value="RECUSATION">Récusation d'un arbitre</option>
            <option value="EXCEPTION_INCOMPETENCE">
              Exception d'incompétence
            </option>
          </select>

          {type === "RECUSATION" && (
            <>
              <select
                value={arbitreCible}
                onChange={(e) => setArbitreCible(e.target.value)}
                disabled={viseTribunal || arbitres.length === 0}
                className="w-full rounded border p-2 text-sm bg-white text-gray-900 disabled:bg-gray-100"
              >
                <option value="">
                  {arbitres.length === 0
                    ? "Aucun arbitre constitué"
                    : "— Arbitre visé —"}
                </option>
                {arbitres.map((a) => (
                  <option key={a.user_id} value={a.user_id}>
                    {(ROLE_LABEL[a.member_role] ?? a.member_role)} —{" "}
                    {a.user_id.slice(0, 8)}…
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={viseTribunal}
                  onChange={(e) => setViseTribunal(e.target.checked)}
                />
                Vise l'ensemble du tribunal
              </label>
            </>
          )}

          <textarea
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            required
            placeholder="Motivation de l'incident (cause de récusation, fondement de l'incompétence…)"
            className="w-full rounded border p-2 text-sm bg-white text-gray-900"
            rows={3}
          />
          <input
            value={fondement}
            onChange={(e) => setFondement(e.target.value)}
            placeholder="Fondement textuel invoqué (facultatif : AUA, Traité, Règlement…)"
            className="w-full rounded border p-2 text-sm bg-white text-gray-900"
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={
                busy ||
                motif.trim().length < 10 ||
                (type === "RECUSATION" && !viseTribunal && !arbitreCible)
              }
              className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm disabled:opacity-50"
            >
              Soumettre l'incident
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-gray-500 text-sm"
            >
              Annuler
            </button>
          </div>
        </form>
      )}

      {incidents.length === 0 ? (
        <p className="text-sm text-gray-400 italic">
          Aucun incident procédural soulevé.
        </p>
      ) : (
        <div className="space-y-3">
          {incidents.map((inc) => {
            const canDecide = inc.statut === "SOULEVEE" && peutTrancher(inc, role);
            // Précédent CCJA joint par ref (peut être absent si non encore chargé).
            const precedent = inc.precedent_ref
              ? precedentsByRef[inc.precedent_ref]
              : undefined;
            return (
              <div key={inc.id} className="border rounded p-3 text-sm">
                <div className="flex justify-between items-start gap-3">
                  <div className="font-medium text-gray-800">
                    {TYPE_LABEL[inc.incident_type]}
                    {inc.incident_type === "RECUSATION" && (
                      <span className="ml-2 text-xs text-gray-500">
                        {inc.vise_tout_le_tribunal
                          ? "· tout le tribunal"
                          : inc.arbitre_cible
                            ? `· arbitre ${inc.arbitre_cible}`
                            : ""}
                      </span>
                    )}
                  </div>
                  <span
                    className={`shrink-0 text-xs px-2 py-1 rounded-full font-bold ${STATUT_STYLE[inc.statut]}`}
                  >
                    {inc.statut}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-1">{inc.motif}</p>
                {inc.fondement_textuel && (
                  <p className="text-xs text-gray-500 mt-1">
                    Fondement : {inc.fondement_textuel}
                  </p>
                )}

                {/* Recommandation jurisprudentielle + précédent CCJA (hydraté) */}
                {canSeeSuggestions &&
                  (inc.recommandation || inc.precedent_ref) && (
                  <div className="mt-2 rounded border border-indigo-200 bg-indigo-50 p-2 text-xs text-indigo-900">
                    <div className="font-semibold">
                      Recommandation consultative
                    </div>
                    {inc.recommandation && (
                      <div>Action suggérée : {inc.recommandation}</div>
                    )}
                    {inc.precedent_ref && (
                      <div>
                        Précédent CCJA : {inc.precedent_ref}
                        {precedent && (
                          <span className="text-indigo-700">
                            {" "}
                            — {precedent.theme}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {inc.decision_motif && (
                  <p className="text-xs text-gray-500 mt-1">
                    Décision : {inc.decision_motif}
                  </p>
                )}

                {canDecide && decId !== inc.id && (
                  <button
                    onClick={() => {
                      setDecId(inc.id);
                      setDecChoice("ADMISE");
                      setDecMotif("");
                    }}
                    className="mt-2 text-xs text-green-700 underline"
                  >
                    Trancher
                  </button>
                )}
                {canDecide && decId === inc.id && (
                  <div className="mt-2 space-y-2">
                    <select
                      value={decChoice}
                      onChange={(e) => setDecChoice(e.target.value)}
                      className="rounded border p-1.5 text-sm bg-white text-gray-900"
                    >
                      <option value="ADMISE">Admise</option>
                      <option value="REJETEE">Rejetée</option>
                      <option value="IRRECEVABLE">Irrecevable</option>
                    </select>
                    {inc.incident_type === "EXCEPTION_INCOMPETENCE" &&
                      decChoice === "ADMISE" && (
                        <p className="text-xs text-red-700">
                          ⚠ Admettre l'incompétence dessaisit le tribunal : la
                          phase passe à DESSAISI (terminale) et le dossier est
                          gelé.
                        </p>
                      )}
                    <textarea
                      value={decMotif}
                      onChange={(e) => setDecMotif(e.target.value)}
                      placeholder="Motif (obligatoire si rejet ou irrecevabilité)"
                      className="w-full rounded border p-2 text-sm bg-white text-gray-900"
                      rows={2}
                    />
                    <div className="flex gap-2">
                      <button
                        disabled={busy}
                        onClick={() => handleDecider(inc.id)}
                        className="bg-green-700 text-white px-3 py-1 rounded text-xs disabled:opacity-50"
                      >
                        Rendre la décision
                      </button>
                      <button
                        onClick={() => setDecId(null)}
                        className="text-gray-500 text-xs"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
